/**
 * ModRouter — keeps modulation wiring in sync with the voice pool.
 *
 * Two kinds of source:
 *
 *   - **Preset-scope** (registered via `registerSource`): one shared AudioNode
 *     fans out to every voice's target AudioParam through a single depth-gain.
 *     LFO, S&H, and eventually mod-wheel live here.
 *
 *          source  →  depthGain  →  [ voice1.target, voice2.target, … ]
 *
 *   - **Per-note** (well-known sentinels: 'velocity', 'keytrack'): each voice
 *     has its *own* source node (a ConstantSourceNode updated on noteOn) and
 *     its own depth-gain. The user-facing depth value is shared across voices.
 *
 *          voice1.source  →  voice1.depthGain  →  voice1.target
 *          voice2.source  →  voice2.depthGain  →  voice2.target
 *          …
 *
 * Both kinds use the same `ModConnection` shape on the preset-facing side —
 * the router picks which wiring path to use based on whether the source id
 * is a per-note sentinel. Voice lifecycle events rewire both kinds correctly.
 */

import type { AudioEngine } from '../AudioEngine';
import type { Voice } from '../Voice';
import type { VoiceManager } from '../VoiceManager';
import type { MacroBank } from '../../macros/MacroBank';
import type { NumericParameter } from '../../params/Parameter';
import {
  clampCurve,
  clampDepth,
  clampRangeMul,
  mintConnectionId,
  shapeValue,
  type ModConnection,
  type ModCurve,
} from './ModConnection';
import { findTarget, getTargetScale, type ModTarget } from './targets';

interface SourceEntry {
  node: AudioNode;
  label: string;
  /** Display grouping tag for the Mod Matrix dropdown (Modulators, Macros, …). */
  category: string;
}

type WiringEntry =
  | {
      kind: 'preset';
      depthGain: GainNode;
      voiceTargets: Map<Voice, AudioParam>;
    }
  | {
      kind: 'per-note';
      perVoice: Map<Voice, GainNode>;
    }
  | {
      /**
       * Preset-scope source → single global AudioParam (on an effect module).
       * No voice lifecycle involvement.
       */
      kind: 'global';
      depthGain: GainNode;
      target: AudioParam;
    }
  | {
      /**
       * Audio-rate source → Parameter-only target (e.g., LFO → macro). Visible
       * knob movement only; no audio propagation since the target exposes no
       * AudioParam. The rAF display sampler tracks the source's current value
       * and writes to targetParam.setModContribution.
       */
      kind: 'display-only';
      targetParam: NumericParameter;
    }
  | {
      /**
       * Macro → target Parameter (not AudioParam). Drives the Parameter's
       * visible value so knobs move when the macro moves, which matches user
       * expectations for slow performance controls. Baseline tracks what the
       * target would be without this macro — updated on user drags so
       * direct target edits co-exist with macro control.
       */
      kind: 'macro';
      macroValue: NumericParameter;
      targetParam: NumericParameter;
      depth: number;
      modScale: number;
      baseline: number;
      curve: ModCurve;
      unsubs: (() => void)[];
    };

/** Per-source display sampler: one AnalyserNode + its scratch buffer. */
interface DisplaySampler {
  readonly analyser: AnalyserNode;
  readonly buffer: Float32Array;
}

export type ModRouterListener = (router: ModRouter) => void;

/**
 * Well-known per-note source sentinels. Voice.getModSourceNode knows how to
 * resolve each of these to a per-voice AudioNode.
 */
const PER_NOTE_SOURCES = [
  { id: 'velocity', label: 'Velocity', category: 'Per-note' },
  { id: 'keytrack', label: 'Keytrack', category: 'Per-note' },
  // The voice's amplitude envelope, exposed as a 0..velocity-peak
  // signal. Routing it to OSC.Coarse with positive depth gives the
  // classic "pitch sweep down with the note" sound (fart, kick drum,
  // synth pluck pitch-pop). Per-note because each voice carries its
  // own envelope independently.
  { id: 'envelope', label: 'Envelope', category: 'Per-note' },
] as const;

const PER_NOTE_IDS: ReadonlySet<string> = new Set(PER_NOTE_SOURCES.map((s) => s.id));

/**
 * True when a connection would assign a macro to its own Value target. The
 * macro wiring path subscribes source → target, so source === target would
 * write in an infinite loop. Keep this a plain function so both addConnection
 * and updateConnection share one rule.
 */
function isMacroSelfAssignment(
  sourceModuleId: string,
  targetModuleId: string,
  targetParam: string,
): boolean {
  return (
    sourceModuleId.startsWith('macro-') &&
    sourceModuleId === targetModuleId &&
    targetParam === 'Value'
  );
}

export class ModRouter {
  private readonly engine: AudioEngine;
  private readonly voiceManager: VoiceManager;
  private targets: readonly ModTarget[];

  private readonly sources = new Map<string, SourceEntry>();
  private readonly connections = new Map<string, ModConnection>();
  private readonly wirings = new Map<string, WiringEntry>();
  private readonly listeners = new Set<ModRouterListener>();

  private readonly unsubVoices: () => void;

  private readonly macroBank: MacroBank | null;

  /**
   * One AnalyserNode per source (shared across all connections using it) so
   * the rAF loop can sample source values and write them to each target's
   * display channel. Created lazily the first time a connection needs display
   * sampling.
   */
  private readonly samplers = new Map<string, DisplaySampler>();
  /**
   * Parameters we've written a nonzero mod contribution to. Tracked so we
   * can zero them out when the contributing connection is torn down.
   */
  private readonly modulatedParams = new Set<NumericParameter>();
  private rafHandle: number | null = null;

  constructor(
    engine: AudioEngine,
    voiceManager: VoiceManager,
    targets: readonly ModTarget[],
    opts: { macroBank?: MacroBank } = {},
  ) {
    this.engine = engine;
    this.voiceManager = voiceManager;
    this.targets = targets;
    this.macroBank = opts.macroBank ?? null;
    this.unsubVoices = voiceManager.subscribeVoices((event, voice) => {
      if (event === 'created') this.wireVoice(voice);
      else this.unwireVoice(voice);
    });
  }

  // ── Targets ──────────────────────────────────────────────────────────

  /** Current target list. Used by the arc controller and tests. */
  listTargets(): readonly ModTarget[] {
    return this.targets;
  }

  /**
   * Replace the target list (e.g. when an effect was added/removed).
   * Connections whose target moduleId is no longer present in the new
   * list are torn down and dropped — there's nothing to wire to. All
   * other connections are rewired so their depth gain reflects the
   * possibly-updated modScale.
   *
   * Listeners fire once at the end with the post-update state.
   */
  setTargets(targets: readonly ModTarget[]): void {
    this.targets = targets;
    // Live moduleIds in the new target set. A connection whose target
    // module isn't in here lost its destination (effect was removed).
    const knownModules = new Set(targets.map((t) => t.moduleId));
    const orphaned: string[] = [];
    for (const conn of this.connections.values()) {
      if (!knownModules.has(conn.targetModuleId)) orphaned.push(conn.id);
    }
    for (const id of orphaned) {
      this.tearDownWiring(id);
      this.connections.delete(id);
    }
    // Surviving connections may need fresh modScale (e.g. an effect's
    // params changed). Rewire each by tearing down and re-running the
    // wiring path; safe because the router is the only owner.
    for (const conn of [...this.connections.values()]) {
      this.wireConnection(conn);
    }
    this.emit();
  }

  // ── Sources ──────────────────────────────────────────────────────────

  registerSource(
    moduleId: string,
    modOutput: AudioNode,
    label: string,
    category: string = 'Modulators',
  ): void {
    this.sources.set(moduleId, { node: modOutput, label, category });
    for (const conn of this.connections.values()) {
      if (conn.sourceModuleId === moduleId && !this.wirings.has(conn.id)) {
        this.wireConnection(conn);
      }
    }
    this.emit();
  }

  unregisterSource(moduleId: string): void {
    this.sources.delete(moduleId);
    for (const conn of [...this.connections.values()]) {
      if (conn.sourceModuleId === moduleId) {
        this.tearDownWiring(conn.id);
        this.connections.delete(conn.id);
      }
    }
    // Drop the display sampler too — the analyser is orphaned and would
    // otherwise keep getting polled forever.
    const sampler = this.samplers.get(moduleId);
    if (sampler) {
      try {
        sampler.analyser.disconnect();
      } catch {
        /* already disconnected */
      }
      this.samplers.delete(moduleId);
    }
    this.emit();
  }

  /**
   * Enumerate every source the user can pick in the Mod Matrix, including
   * the built-in per-note sentinels.
   */
  listSources(): Array<{ id: string; label: string; category: string }> {
    const registered = [...this.sources.entries()].map(([id, { label, category }]) => ({
      id,
      label,
      category,
    }));
    const perNote = PER_NOTE_SOURCES.map((s) => ({
      id: s.id,
      label: s.label,
      category: s.category,
    }));
    return [...registered, ...perNote];
  }

  // ── Connections ──────────────────────────────────────────────────────

  addConnection(spec: Omit<ModConnection, 'id'>): string {
    // Refuse a macro assigned to its own Value target: the macro wiring path
    // subscribes the source's value changes to write back to the target,
    // which would loop forever when source === target. Blocking here makes
    // both UI and programmatic calls safe.
    if (isMacroSelfAssignment(spec.sourceModuleId, spec.targetModuleId, spec.targetParam)) {
      return '';
    }
    const id = mintConnectionId();
    const conn: ModConnection = {
      id,
      ...spec,
      depth: clampDepth(spec.depth),
      ...(spec.rangeMul !== undefined
        ? { rangeMul: clampRangeMul(spec.rangeMul) }
        : {}),
      ...(spec.curve !== undefined ? { curve: clampCurve(spec.curve) } : {}),
    };
    this.connections.set(id, conn);
    this.wireConnection(conn);
    this.emit();
    return id;
  }

  removeConnection(id: string): void {
    if (!this.connections.has(id)) return;
    this.tearDownWiring(id);
    this.connections.delete(id);
    this.emit();
  }

  /**
   * Change a connection's source and/or target in place. Depth and id are
   * preserved; wiring is torn down and rebuilt, which cleanly handles a
   * source-kind switch (preset ↔ per-note). Unknown fields in `update` are
   * ignored so callers can pass a partial.
   */
  updateConnection(
    id: string,
    update: {
      sourceModuleId?: string;
      targetModuleId?: string;
      targetParam?: string;
    },
  ): void {
    const conn = this.connections.get(id);
    if (!conn) return;
    const next: ModConnection = {
      ...conn,
      sourceModuleId: update.sourceModuleId ?? conn.sourceModuleId,
      targetModuleId: update.targetModuleId ?? conn.targetModuleId,
      targetParam: update.targetParam ?? conn.targetParam,
    };
    if (
      next.sourceModuleId === conn.sourceModuleId &&
      next.targetModuleId === conn.targetModuleId &&
      next.targetParam === conn.targetParam
    ) {
      return;
    }
    // Reject edits that would land on a macro's self-assignment.
    if (isMacroSelfAssignment(next.sourceModuleId, next.targetModuleId, next.targetParam)) {
      return;
    }
    this.connections.set(id, next);
    this.wireConnection(next); // tearDown-then-rewire is built in
    this.emit();
  }

  setDepth(id: string, depth: number): void {
    const conn = this.connections.get(id);
    if (!conn) return;
    const clamped = clampDepth(depth);
    const next = { ...conn, depth: clamped };
    this.connections.set(id, next);
    this.applyEffectiveDepth(id, next);
    this.emit();
  }

  /**
   * Set the per-connection range multiplier (0..1). Combines with the
   * connection's `depth` to scale the effective modulation amount —
   * useful for fine mods (e.g. ±5ms chorus sweep on delay time) where
   * the raw depth slider would be too coarse.
   */
  setRangeMul(id: string, rangeMul: number): void {
    const conn = this.connections.get(id);
    if (!conn) return;
    const clamped = clampRangeMul(rangeMul);
    const next = { ...conn, rangeMul: clamped };
    this.connections.set(id, next);
    this.applyEffectiveDepth(id, next);
    this.emit();
  }

  /**
   * Set the per-connection response curve. Only changes audible
   * behavior for macro sources — audio-path connections (LFO, S&H,
   * per-note) ignore the curve since their wiring runs through
   * AudioParams, not the JS macro path. Recomputes the wired macro
   * contribution so the next render reflects the new shape; the
   * baseline gets re-anchored so a curve change at a non-zero macro
   * position doesn't snap the target value.
   */
  setCurve(id: string, curve: ModCurve): void {
    const conn = this.connections.get(id);
    if (!conn) return;
    const clamped = clampCurve(curve);
    const next = { ...conn, curve: clamped };
    this.connections.set(id, next);
    const wiring = this.wirings.get(id);
    if (wiring && wiring.kind === 'macro') {
      // Re-anchor baseline so the existing target value stays put
      // through the curve swap. Otherwise switching exp → linear at
      // macro=0.5 would snap the cutoff up by ~50% of the modScale.
      const oldContribution =
        shapeValue(wiring.macroValue.displayValue, wiring.curve) *
        wiring.depth *
        wiring.modScale;
      const newContribution =
        shapeValue(wiring.macroValue.displayValue, clamped) *
        wiring.depth *
        wiring.modScale;
      wiring.curve = clamped;
      wiring.baseline += oldContribution - newContribution;
      this.applyEffectiveDepth(id, next);
    }
    this.emit();
  }

  /**
   * Recompute the effective-depth-driven audio gain (or macro
   * contribution) for a wiring. Called after depth OR rangeMul change,
   * since both feed the same product.
   */
  private applyEffectiveDepth(id: string, conn: ModConnection): void {
    const wiring = this.wirings.get(id);
    if (!wiring) return;
    const ctx = this.engine.context;
    const t = ctx.currentTime;
    const modScale = getTargetScale(
      this.targets,
      conn.targetModuleId,
      conn.targetParam,
    );
    const effective = this.effectiveDepth(conn);
    const scaled = effective * modScale;
    const write = (ap: AudioParam): void => {
      ap.cancelScheduledValues(t);
      ap.setTargetAtTime(scaled, t, 0.01);
    };
    switch (wiring.kind) {
      case 'preset':
      case 'global':
        write(wiring.depthGain.gain);
        break;
      case 'per-note':
        for (const g of wiring.perVoice.values()) write(g.gain);
        break;
      case 'display-only':
        // No AudioParam to write; rAF sweep picks up the new effective
        // depth via conn on its next tick.
        break;
      case 'macro': {
        // Macros don't sum at an AudioParam; recompute target value.
        wiring.depth = effective;
        const contribution =
          shapeValue(wiring.macroValue.displayValue, wiring.curve) *
          wiring.depth *
          wiring.modScale;
        wiring.targetParam.set(wiring.baseline + contribution, 'macro');
        break;
      }
    }
  }

  listConnections(): ModConnection[] {
    return [...this.connections.values()];
  }

  // ── Lifecycle ────────────────────────────────────────────────────────

  subscribe(listener: ModRouterListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  dispose(): void {
    this.unsubVoices();
    for (const id of [...this.connections.keys()]) this.tearDownWiring(id);
    this.connections.clear();
    this.sources.clear();
    this.listeners.clear();
    // Stop the display loop and release analysers so the rAF loop doesn't
    // keep running and we don't leak audio graph nodes.
    if (this.rafHandle !== null && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this.rafHandle);
    }
    this.rafHandle = null;
    for (const s of this.samplers.values()) {
      try {
        s.analyser.disconnect();
      } catch {
        /* already disconnected */
      }
    }
    this.samplers.clear();
    for (const p of this.modulatedParams) p.setModContribution(0);
    this.modulatedParams.clear();
  }

  // ── Internals ────────────────────────────────────────────────────────

  private isPerNoteSource(sourceId: string): boolean {
    return PER_NOTE_IDS.has(sourceId);
  }

  private scaleFor(conn: ModConnection): number {
    return getTargetScale(this.targets, conn.targetModuleId, conn.targetParam);
  }

  /**
   * User-facing depth × per-connection range multiplier. Every downstream
   * computation multiplies this by the target's modScale to get the
   * final modulation amount in native units.
   */
  private effectiveDepth(conn: ModConnection): number {
    return conn.depth * (conn.rangeMul ?? 1);
  }

  private wireConnection(conn: ModConnection): void {
    if (this.wirings.has(conn.id)) this.tearDownWiring(conn.id);

    const target = findTarget(this.targets, conn.targetModuleId, conn.targetParam);

    // Macros drive target Parameter values directly (visible knob movement).
    // Matches user expectations for slow performance controls.
    const macro = this.findMacro(conn.sourceModuleId);
    if (macro && target?.getParameter) {
      this.wireMacroConnection(conn, target, macro);
      return;
    }

    if (target?.scope === 'global') {
      // Per-note sources summing into a single global AudioParam is non-musical
      // (each voice's contribution stacks). Skip silently — the connection is
      // stored but no wiring is created. Users will only pair global targets
      // with preset-scope sources (LFO, S&H).
      if (this.isPerNoteSource(conn.sourceModuleId)) return;
      const ap = target.getAudioParam?.();
      if (ap) {
        this.wireGlobalConnection(conn, target);
      } else if (target.getParameter) {
        // Audio-rate source targeting a Parameter-only destination (macros).
        // No audio wiring — just a display channel so the knob visibly
        // reflects the source's current value. The rAF sampler does the work.
        const p = target.getParameter();
        if (p) this.wirings.set(conn.id, { kind: 'display-only', targetParam: p });
      }
      this.ensureDisplaySampling(conn.sourceModuleId);
      return;
    }

    if (this.isPerNoteSource(conn.sourceModuleId)) {
      this.wirePerNoteConnection(conn);
    } else {
      this.wirePresetConnection(conn);
      // Preset-scope audio sources (LFO, S&H) should make the target knob
      // visibly wobble — register the source with the display sampler.
      this.ensureDisplaySampling(conn.sourceModuleId);
    }
  }

  /**
   * Make sure the given source has an AnalyserNode tap so the rAF loop can
   * read its instantaneous value for display. No-op if the source isn't
   * registered, or if the context can't create an analyser (e.g., in tests
   * running against the audio shim without that method). Starts the rAF loop
   * on first use.
   */
  private ensureDisplaySampling(sourceId: string): void {
    if (this.samplers.has(sourceId)) return;
    const source = this.sources.get(sourceId);
    if (!source) return;
    const ctx = this.engine.context as unknown as {
      createAnalyser?: () => AnalyserNode;
    };
    if (typeof ctx.createAnalyser !== 'function') return;
    const analyser = ctx.createAnalyser();
    // fftSize=32 is the minimum; we just need a handful of samples to read
    // the latest one. Bigger buffers waste memory for no readout benefit.
    analyser.fftSize = 32;
    try {
      source.node.connect(analyser);
    } catch {
      /* already connected or shim doesn't support — either way, harmless */
    }
    this.samplers.set(sourceId, {
      analyser,
      buffer: new Float32Array(analyser.fftSize),
    });
    this.startDisplayLoop();
  }

  /** Kick off the display-sampling rAF loop if it isn't already running. */
  private startDisplayLoop(): void {
    if (this.rafHandle !== null) return;
    if (typeof requestAnimationFrame === 'undefined') return;
    this.rafHandle = requestAnimationFrame(this.sampleDisplay);
  }

  /**
   * Single sweep of the display sampler. Runs once per animation frame:
   *
   *   1. Read the latest sample from each source's analyser.
   *   2. For each audio-rate wiring (preset / global / per-note / display-only)
   *      compute its contribution to its target Parameter.
   *   3. Write the summed contribution to each target's display channel; zero
   *      out any target that used to be modulated but no longer is.
   */
  private readonly sampleDisplay = (): void => {
    // Step 1 — sample each source.
    const samples = new Map<string, number>();
    for (const [id, s] of this.samplers) {
      // Cast through `unknown` because the DOM typings require
      // Float32Array<ArrayBuffer> but TS's default lib types `new Float32Array`
      // as Float32Array<ArrayBufferLike>. Safe at runtime.
      s.analyser.getFloatTimeDomainData(
        s.buffer as unknown as Float32Array<ArrayBuffer>,
      );
      samples.set(id, s.buffer[s.buffer.length - 1] ?? 0);
    }

    // Step 2 — aggregate contributions per target Parameter. Macro wirings
    // drive _value directly, so they don't contribute here.
    const contribsByTarget = new Map<NumericParameter, number>();
    for (const [connId, wiring] of this.wirings) {
      if (wiring.kind === 'macro') continue;
      const conn = this.connections.get(connId);
      if (!conn) continue;
      const target = findTarget(this.targets, conn.targetModuleId, conn.targetParam);
      const param = target?.getParameter?.();
      if (!param || !target) continue;
      const sample = samples.get(conn.sourceModuleId) ?? 0;
      const contribution = sample * this.effectiveDepth(conn) * target.modScale;
      contribsByTarget.set(
        param,
        (contribsByTarget.get(param) ?? 0) + contribution,
      );
    }

    // Step 3 — publish and clean up.
    const touched = new Set<NumericParameter>();
    for (const [param, sum] of contribsByTarget) {
      param.setModContribution(sum);
      touched.add(param);
    }
    for (const p of this.modulatedParams) {
      if (!touched.has(p)) p.setModContribution(0);
    }
    this.modulatedParams.clear();
    for (const p of touched) this.modulatedParams.add(p);

    // Keep ticking as long as there's at least one sampler. Sources can come
    // and go (registerSource / unregisterSource), so the loop self-terminates
    // when everything's gone.
    if (this.samplers.size > 0 && typeof requestAnimationFrame !== 'undefined') {
      this.rafHandle = requestAnimationFrame(this.sampleDisplay);
    } else {
      this.rafHandle = null;
    }
  };

  private findMacro(sourceId: string): NumericParameter | null {
    if (!this.macroBank) return null;
    const macro = this.macroBank.macros.find((m) => m.id === sourceId);
    return macro?.value ?? null;
  }

  private wireMacroConnection(
    conn: ModConnection,
    target: ModTarget,
    macroValue: NumericParameter,
  ): void {
    const targetParam = target.getParameter?.();
    if (!targetParam) return;

    const effective = this.effectiveDepth(conn);
    const curve = clampCurve(conn.curve);
    const wiring: Extract<WiringEntry, { kind: 'macro' }> = {
      kind: 'macro',
      macroValue,
      targetParam,
      // `depth` here holds the *effective* depth (user depth × rangeMul).
      // setDepth and setRangeMul both keep it current.
      depth: effective,
      modScale: target.modScale,
      baseline:
        targetParam.value -
        shapeValue(macroValue.displayValue, curve) * effective * target.modScale,
      curve,
      unsubs: [],
    };

    const applyMacro = (): void => {
      // Read displayValue, not .value — that way if the source macro is
      // itself being wobbled by an LFO (which writes to setModContribution,
      // affecting displayValue but not .value), the wobble propagates
      // through to this target. This is what makes "LFO → Macro → Cutoff"
      // actually move the Cutoff audibly. shapeValue applies the
      // per-connection curve so the same macro position reaches each
      // target through whatever response shape the user picked.
      const srcValue = shapeValue(wiring.macroValue.displayValue, wiring.curve);
      const contribution = srcValue * wiring.depth * wiring.modScale;
      wiring.targetParam.set(wiring.baseline + contribution, 'macro');
    };

    // Move the target whenever this macro moves — in either channel. Value
    // changes come from user drags and macro-chaining writes; display changes
    // come from audio-rate sources writing setModContribution. Firing both
    // means the target follows whichever path the macro was nudged down.
    wiring.unsubs.push(wiring.macroValue.subscribe(applyMacro));
    wiring.unsubs.push(wiring.macroValue.subscribeDisplay(applyMacro));

    // User drags on the target update our baseline so direct control of the
    // knob keeps working while a macro is assigned. Only value changes
    // count here — display-channel updates on the target are incoming mods,
    // not user intent.
    wiring.unsubs.push(
      wiring.targetParam.subscribe((v, source) => {
        // Only react to "base" writes — our own macro-sourced writes, and
        // other macros on the same target, shouldn't mutate our baseline.
        if (source === 'macro') return;
        const contribution =
          shapeValue(wiring.macroValue.displayValue, wiring.curve) *
          wiring.depth *
          wiring.modScale;
        wiring.baseline = v - contribution;
      }),
    );

    this.wirings.set(conn.id, wiring);
    applyMacro();
  }

  private wireGlobalConnection(conn: ModConnection, target: ModTarget): void {
    const source = this.sources.get(conn.sourceModuleId);
    if (!source) return;
    const ap = target.getAudioParam?.();
    if (!ap) return;

    const ctx = this.engine.context;
    const depthGain = ctx.createGain();
    depthGain.gain.value = this.effectiveDepth(conn) * target.modScale;
    source.node.connect(depthGain);
    depthGain.connect(ap);

    this.wirings.set(conn.id, { kind: 'global', depthGain, target: ap });
  }

  private wirePresetConnection(conn: ModConnection): void {
    const source = this.sources.get(conn.sourceModuleId);
    if (!source) return; // source not registered — will wire on registerSource

    const ctx = this.engine.context;
    const scale = this.scaleFor(conn);
    const depthGain = ctx.createGain();
    depthGain.gain.value = this.effectiveDepth(conn) * scale;
    source.node.connect(depthGain);

    const voiceTargets = new Map<Voice, AudioParam>();
    for (const voice of this.voiceManager.voiceList) {
      const target = voice.getAudioParam(conn.targetModuleId, conn.targetParam);
      if (!target) continue;
      depthGain.connect(target);
      voiceTargets.set(voice, target);
    }

    this.wirings.set(conn.id, { kind: 'preset', depthGain, voiceTargets });
  }

  private wirePerNoteConnection(conn: ModConnection): void {
    const ctx = this.engine.context;
    const scale = this.scaleFor(conn);
    const perVoice = new Map<Voice, GainNode>();

    for (const voice of this.voiceManager.voiceList) {
      const src = voice.getModSourceNode(conn.sourceModuleId);
      const target = voice.getAudioParam(conn.targetModuleId, conn.targetParam);
      if (!src || !target) continue;
      const dg = ctx.createGain();
      dg.gain.value = this.effectiveDepth(conn) * scale;
      src.connect(dg);
      dg.connect(target);
      perVoice.set(voice, dg);
    }

    this.wirings.set(conn.id, { kind: 'per-note', perVoice });
  }

  private tearDownWiring(id: string): void {
    const wiring = this.wirings.get(id);
    if (!wiring) return;
    try {
      switch (wiring.kind) {
        case 'preset':
        case 'global':
          wiring.depthGain.disconnect();
          break;
        case 'per-note':
          for (const g of wiring.perVoice.values()) g.disconnect();
          break;
        case 'display-only':
          // No audio wiring to disconnect; reset the display so the knob
          // doesn't freeze at the last modulated position.
          wiring.targetParam.setModContribution(0);
          this.modulatedParams.delete(wiring.targetParam);
          break;
        case 'macro':
          for (const u of wiring.unsubs) u();
          // Restore the target to its baseline so removing the macro doesn't
          // leave the knob at whatever the last contributed position was.
          wiring.targetParam.set(wiring.baseline, 'macro');
          break;
      }
    } catch {
      /* already disconnected */
    }
    this.wirings.delete(id);
  }

  private wireVoice(voice: Voice): void {
    for (const [connId, wiring] of this.wirings) {
      const conn = this.connections.get(connId);
      if (!conn) continue;
      switch (wiring.kind) {
        case 'global':
        case 'macro':
        case 'display-only':
          // These wirings don't participate in voice lifecycle.
          break;
        case 'preset': {
          const target = voice.getAudioParam(conn.targetModuleId, conn.targetParam);
          if (!target) continue;
          wiring.depthGain.connect(target);
          wiring.voiceTargets.set(voice, target);
          break;
        }
        case 'per-note': {
          const src = voice.getModSourceNode(conn.sourceModuleId);
          const target = voice.getAudioParam(conn.targetModuleId, conn.targetParam);
          if (!src || !target) continue;
          const ctx = this.engine.context;
          const dg = ctx.createGain();
          dg.gain.value = this.effectiveDepth(conn) * this.scaleFor(conn);
          src.connect(dg);
          dg.connect(target);
          wiring.perVoice.set(voice, dg);
          break;
        }
      }
    }
  }

  private unwireVoice(voice: Voice): void {
    for (const wiring of this.wirings.values()) {
      switch (wiring.kind) {
        case 'global':
        case 'macro':
        case 'display-only':
          break;
        case 'preset': {
          const target = wiring.voiceTargets.get(voice);
          if (!target) continue;
          try {
            wiring.depthGain.disconnect(target);
          } catch {
            /* already detached */
          }
          wiring.voiceTargets.delete(voice);
          break;
        }
        case 'per-note': {
          const dg = wiring.perVoice.get(voice);
          if (!dg) continue;
          try {
            dg.disconnect();
          } catch {
            /* already detached */
          }
          wiring.perVoice.delete(voice);
          break;
        }
      }
    }
  }

  private emit(): void {
    for (const fn of this.listeners) fn(this);
  }
}
